/**
 * InsuranceConfigView - Admin Configuration View for Insurance Module
 *
 * Admin-grade insurance management:
 * - 5 tabs: Contrats, Véhicules Assurés, Catalogue Produits, Intermédiaires, Modèles de Couverture
 * - Full CRUD, workflow actions, vehicle assignment tools
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, FileText, Users, Layers, Car, Shield } from 'lucide-react';
import ContractsTab from './tabs/ContractsTab.jsx';
import VehicleInsuranceTab from './tabs/VehicleInsuranceTab.jsx';
import InsuranceCatalogTab from './tabs/InsuranceCatalogTab.jsx';
import IntermediariesTab from './tabs/IntermediariesTab.jsx';
import CoverageBlocksTab from './tabs/CoverageBlocksTab.jsx';

// labelKey (et non label) : une constante de module est évaluée une seule fois,
// avant tout changement de langue — le libellé se résout au rendu via t().
const CONFIG_TABS = [
  {
    id: 'contracts',
    labelKey: 'insuranceConfig.tabs.contracts',
    icon: Shield,
  },
  {
    id: 'vehicle_insurance',
    labelKey: 'insuranceConfig.tabs.vehicleInsurance',
    icon: Car,
  },
  {
    id: 'catalog',
    labelKey: 'insuranceConfig.tabs.catalog',
    icon: FileText,
  },
  {
    id: 'intermediaries',
    labelKey: 'insuranceConfig.tabs.intermediaries',
    icon: Users,
  },
  {
    id: 'coverage',
    labelKey: 'insuranceConfig.tabs.coverage',
    icon: Layers,
  },
];

function InsuranceConfigView({ onMessage }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('contracts');

  return (
    <div className="space-y-6" data-tour="insurance-config-view">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Settings className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('insuranceConfig.view.title')}
            </h1>
            <p className="text-gray-500 mt-1">
              {t('insuranceConfig.view.subtitle')}
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-t border-gray-200 pt-4">
          <div className="flex space-x-1">
            {CONFIG_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div data-tour="insurance-config-content">
        {activeTab === 'contracts' && <ContractsTab onMessage={onMessage} />}
        {activeTab === 'vehicle_insurance' && <VehicleInsuranceTab onMessage={onMessage} />}
      </div>
      {(activeTab === 'catalog' || activeTab === 'intermediaries' || activeTab === 'coverage') && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {activeTab === 'catalog' && <InsuranceCatalogTab onMessage={onMessage} />}
          {activeTab === 'intermediaries' && <IntermediariesTab onMessage={onMessage} />}
          {activeTab === 'coverage' && <CoverageBlocksTab onMessage={onMessage} />}
        </div>
      )}
    </div>
  );
}

export default InsuranceConfigView;
